import React, { useState, useRef, useEffect } from "react";
// Switched off Bootstrap's Form.Control / Button in favour of native elements
// with explicit inline styles, so the input height (34px), border treatment,
// and focus ring match the rest of the modernised student-search page.
import MultiSelect from "./MultiSelect";
import StudentSearchService from "../../_services/StudentSearchService";

interface BatchSuggestion {
  id: number;
  name: string;
  batchCode?: string | null;
  displayName?: string | null;
}

interface SearchToolbarProps {
  lookups: any;
  searchParams: any;
  onParamChange: (key: string, value: any) => void;
  onSearch: () => void;
  onToggleAdvanced: () => void;
  showAdvancedFilters: boolean;
  loading: boolean;
  onExcel: () => void;
  onBulkUpdate: () => void;
  onBulkMessage: (channel: string) => void;
  onBulkStatusChange: () => void;
  onBulkPromote: () => void;
  onBulkAddAnotherCourse: () => void;
  onBulkAssignTags: () => void;
  onBulkAssignMentor: () => void;
  onBulkAssignStaff: () => void;
  onBulkAddSubBatch: () => void;
  onBulkStudentPhotos: () => void;
  onBulkHealthDetails: () => void;
  onBulkAddDemand: () => void;
  onBulkIssueMaterial: () => void;
  onBulkDiscount: () => void;
  onBulkTransferAlumni: () => void;
  onBulkIssueTc: () => void;
  onBulkIdCard: () => void;
  onBulkGradeReport: () => void;
  onStatementOfAccount: () => void;
  onReminderSlip: () => void;
  onUpdateTransportPlan: () => void;
  onBulkChangePlacementStatus: () => void;
  onBulkAssignStreams: () => void;
  onBulkAssignModules: () => void;
  onBulkAssignElectives: () => void;
  onBulkDropModules: () => void;
  onBulkTransfer: () => void;
  onPasswordSearch: () => void;
  selectedCount?: number;
}

/* The full legacy bulk-operation set (from BulkOperationButtons.jsp), grouped for the
   "Bulk Actions" menu. Items with `enabled: true` are wired to a real nodefork route +
   service + modal; the remaining ones render disabled with a "Soon" pill until each is
   built (one nodefork route + service + modal per operation). */
interface BulkActionItem {
  key: string;
  label: string;
  icon: string; // font-awesome class
  enabled?: boolean;
}
interface BulkActionGroup {
  group: string;
  items: BulkActionItem[];
}
const BULK_ACTION_GROUPS: BulkActionGroup[] = [
  {
    group: "Enrollment",
    items: [
      { key: "change-status", label: "Change Status", icon: "fa-exchange", enabled: true },
      { key: "promote", label: "Promote", icon: "fa-level-up", enabled: true },
      { key: "transfer", label: "Transfer", icon: "fa-random", enabled: true },
      { key: "transfer-alumni", label: "Transfer to Alumni", icon: "fa-user-circle-o", enabled: true },
      { key: "issue-tc", label: "Issue TC", icon: "fa-file-text-o", enabled: true },
    ],
  },
  {
    group: "Academics",
    items: [
      { key: "grade-report", label: "Grade Report", icon: "fa-bar-chart", enabled: true },
      { key: "assign-electives", label: "Assign Electives", icon: "fa-check-square-o", enabled: true },
      { key: "assign-modules", label: "Assign Modules", icon: "fa-plus-square-o", enabled: true },
      { key: "drop-modules", label: "Drop Modules", icon: "fa-minus-square-o", enabled: true },
      { key: "assign-streams", label: "Assign Streams", icon: "fa-sitemap", enabled: true },
    ],
  },
  {
    group: "Tags & Staff",
    items: [
      { key: "assign-tags", label: "Assign Tags", icon: "fa-tags", enabled: true },
      // { key: "assign-staff", label: "Assign Staff", icon: "fa-user-plus", enabled: true },
      { key: "assign-mentor", label: "Assign Mentor", icon: "fa-graduation-cap", enabled: true },
    ],
  },
  {
    group: "Finance",
    items: [
      { key: "statement-of-account", label: "Statement of Account", icon: "fa-file-text", enabled: true },
      { key: "reminder-slip", label: "Reminder Slip", icon: "fa-bell-o", enabled: true },
      { key: "bulk-discount", label: "Bulk Discount", icon: "fa-percent", enabled: true },
      { key: "add-demand", label: "Add Demand", icon: "fa-money", enabled: true },
    ],
  },
  {
    group: "Documents & Other",
    items: [
      { key: "id-card", label: "Id Card", icon: "fa-id-card-o", enabled: true },
      { key: "student-photos", label: "Student Photos", icon: "fa-camera", enabled: true },
      { key: "health-details", label: "Health Details", icon: "fa-heartbeat", enabled: true },
      { key: "add-sub-batch", label: "Add Sub Batch", icon: "fa-object-group", enabled: true },
      { key: "issue-bulk-material", label: "Issue Bulk Material", icon: "fa-cubes", enabled: true },
      { key: "update-transport-template", label: "Update Transport Plan", icon: "fa-bus", enabled: true },
      { key: "change-placement-status", label: "Change Placement Status", icon: "fa-briefcase", enabled: true },
      { key: "add-another-course", label: "Add Another Course", icon: "fa-plus-circle", enabled: true },
    ],
  },
];

const SearchToolbar: React.FC<SearchToolbarProps> = ({
  lookups,
  searchParams,
  onParamChange,
  onSearch,
  onToggleAdvanced,
  showAdvancedFilters,
  loading,
  onExcel,
  onBulkUpdate,
  onBulkMessage,
  onBulkStatusChange,
  onBulkPromote,
  onBulkAddAnotherCourse,
  onBulkAssignTags,
  onBulkAssignMentor,
  onBulkAssignStaff,
  onBulkAddSubBatch,
  onBulkStudentPhotos,
  onBulkHealthDetails,
  onBulkAddDemand,
  onBulkIssueMaterial,
  onBulkDiscount,
  onBulkTransferAlumni,
  onBulkIssueTc,
  onBulkIdCard,
  onBulkGradeReport,
  onStatementOfAccount,
  onReminderSlip,
  onUpdateTransportPlan,
  onBulkChangePlacementStatus,
  onBulkAssignStreams,
  onBulkAssignModules,
  onBulkAssignElectives,
  onBulkDropModules,
  onBulkTransfer,
  onPasswordSearch,
  selectedCount = 0,
}) => {
  const [showNewEnquiryMenu, setShowNewEnquiryMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const bulkMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showNewEnquiryMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowNewEnquiryMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showNewEnquiryMenu]);

  // Close the Bulk Actions menu when clicking outside
  useEffect(() => {
    if (!showBulkMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target as Node)) {
        setShowBulkMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showBulkMenu]);

  // ── Batch type-ahead ────────────────────────────────────────────────────
  // The Batch filter no longer preloads every batch. As the user types (name or
  // code), we debounce-fetch matching batches from /student-search/batches and
  // render our own suggestion list (native <datalist> can't be driven by a
  // remote source without the browser re-filtering and hiding server results).
  const [batchSuggestions, setBatchSuggestions] = useState<BatchSuggestion[]>([]);
  const [showBatchDropdown, setShowBatchDropdown] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const batchBoxRef = useRef<HTMLDivElement>(null);
  const batchQueryRef = useRef<string>("");

  useEffect(() => {
    const term = (searchParams.batchCode || "").trim();
    batchQueryRef.current = term;
    if (term.length < 2) {
      setBatchSuggestions([]);
      setBatchLoading(false);
      return;
    }
    setBatchLoading(true);
    const handle = setTimeout(async () => {
      try {
        const resp = await StudentSearchService.searchBatches(term);
        // Ignore stale responses if the user kept typing.
        if (batchQueryRef.current !== term) return;
        setBatchSuggestions(resp?.data?.data || []);
      } catch {
        if (batchQueryRef.current === term) setBatchSuggestions([]);
      } finally {
        if (batchQueryRef.current === term) setBatchLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [searchParams.batchCode]);

  // Close the batch suggestion list when clicking outside.
  useEffect(() => {
    if (!showBatchDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (batchBoxRef.current && !batchBoxRef.current.contains(e.target as Node)) {
        setShowBatchDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showBatchDropdown]);

  const selectBatch = (b: BatchSuggestion) => {
    // Store the batch name — the search WHERE matches name/code/displayName.
    onParamChange("batchCode", b.name);
    setShowBatchDropdown(false);
  };

  const batchLabel = (b: BatchSuggestion) => {
    const code = (b.batchCode || "").trim();
    let label = b.name;
    if (code && code !== b.name) label += ` · ${code}`;
    return label;
  };

  // Route an enabled bulk action to its handler. Unbuilt actions are inert ("Soon").
  const runBulkAction = (key: string) => {
    setShowBulkMenu(false);
    if (key === "change-status") onBulkStatusChange();
    else if (key === "promote") onBulkPromote();
    else if (key === "add-another-course") onBulkAddAnotherCourse();
    else if (key === "assign-tags") onBulkAssignTags();
    else if (key === "assign-mentor") onBulkAssignMentor();
    else if (key === "assign-staff") onBulkAssignStaff();
    else if (key === "add-sub-batch") onBulkAddSubBatch();
    else if (key === "student-photos") onBulkStudentPhotos();
    else if (key === "health-details") onBulkHealthDetails();
    else if (key === "add-demand") onBulkAddDemand();
    else if (key === "issue-bulk-material") onBulkIssueMaterial();
    else if (key === "bulk-discount") onBulkDiscount();
    else if (key === "transfer-alumni") onBulkTransferAlumni();
    else if (key === "issue-tc") onBulkIssueTc();
    else if (key === "id-card") onBulkIdCard();
    else if (key === "grade-report") onBulkGradeReport();
    else if (key === "statement-of-account") onStatementOfAccount();
    else if (key === "reminder-slip") onReminderSlip();
    else if (key === "update-transport-template") onUpdateTransportPlan();
    else if (key === "change-placement-status") onBulkChangePlacementStatus();
    else if (key === "assign-streams") onBulkAssignStreams();
    else if (key === "assign-modules") onBulkAssignModules();
    else if (key === "assign-electives") onBulkAssignElectives();
    else if (key === "drop-modules") onBulkDropModules();
    else if (key === "transfer") onBulkTransfer();
  };

  const staffWebforms = (lookups?.webforms || []).filter(
    (w: any) => w.restrictedToStaff === 1,
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSearch();
    }
  };

  /* Shared input style for the text fields in the toolbar so every input
     matches the height + border treatment of the new card-style results
     table. Bootstrap's sm inputs were uneven against the MultiSelect dropdowns;
     these explicit values normalise everything to a 34px control band. */
  const toolbarInputStyle: React.CSSProperties = {
    height: 34,
    padding: "0 10px",
    fontSize: "0.85rem",
    borderRadius: 6,
    border: "1px solid var(--border-strong)",
    background: "var(--surface-card)",
    color: "var(--text-strong)",
    outline: "none",
    transition: "border-color .12s, box-shadow .12s",
  };
  const onInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = "var(--brand-primary)";
    e.currentTarget.style.boxShadow = "0 0 0 3px rgba(59,130,246,0.15)";
  };
  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = "var(--border-strong)";
    e.currentTarget.style.boxShadow = "none";
  };

  return (
    <div
      style={{
        background: "var(--surface-card)",
        padding: "12px 16px",
        borderRadius: 12,
        marginBottom: 12,
        border: "1px solid var(--border-default)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {/* Hamburger toggle for advanced filters */}
        <button
          type="button"
          onClick={onToggleAdvanced}
          title="Toggle Advanced Filters"
          style={{
            cursor: "pointer",
            width: 34,
            height: 34,
            border: showAdvancedFilters ? "1px solid #3b82f6" : "1px solid #e2e8f0",
            background: showAdvancedFilters ? "#eff6ff" : "#fff",
            borderRadius: 6,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 4,
            padding: 0,
          }}
        >
          <span style={{ display: "block", width: 18, height: 2, background: showAdvancedFilters ? "#1d4ed8" : "#475569", borderRadius: 2 }} />
          <span style={{ display: "block", width: 18, height: 2, background: showAdvancedFilters ? "#1d4ed8" : "#475569", borderRadius: 2 }} />
          <span style={{ display: "block", width: 18, height: 2, background: showAdvancedFilters ? "#1d4ed8" : "#475569", borderRadius: 2 }} />
        </button>

        {/* EnquiryId / Name / Email */}
        <input
          type="text"
          placeholder="EnquiryId / Name / Email"
          value={searchParams.enquiryIdOrNameOrEmail || ""}
          onChange={(e) => onParamChange("enquiryIdOrNameOrEmail", e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          style={{ ...toolbarInputStyle, width: 200 }}
        />

        {/* Student Code */}
        <input
          type="text"
          placeholder="Student Code"
          value={searchParams.studentCode || ""}
          onChange={(e) => onParamChange("studentCode", e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          style={{ ...toolbarInputStyle, width: 140 }}
        />

        {/* Username */}
        <input
          type="text"
          placeholder="Username"
          value={searchParams.userName || ""}
          onChange={(e) => onParamChange("userName", e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          style={{ ...toolbarInputStyle, width: 140 }}
        />

        {/* Year - Multi-select */}
        <MultiSelect
          options={lookups?.years || []}
          selectedIds={searchParams.yearIds || []}
          onChange={(ids) => onParamChange("yearIds", ids)}
          placeholder="All Years"
          maxWidth={140}
        />

        {/* School/Centre - Multi-select */}
        <MultiSelect
          options={lookups?.schools || []}
          selectedIds={searchParams.schoolIds || []}
          onChange={(ids) => {
            onParamChange("schoolIds", ids);
            onParamChange("schoolType", "school");
          }}
          placeholder="All Centres"
          maxWidth={160}
        />

        {/* Course / Program - Multi-select */}
        {lookups?.isProgramBased ? (
          <MultiSelect
            options={(lookups?.programs || []).map((p: any) => ({
              id: p.id,
              name: p.shortCode ? `${p.name} [${p.shortCode}]` : p.name,
            }))}
            selectedIds={searchParams.programIds || []}
            onChange={(ids) => onParamChange("programIds", ids)}
            placeholder="All Programs"
            maxWidth={160}
          />
        ) : (
          <MultiSelect
            options={lookups?.courses || []}
            selectedIds={searchParams.courseIds || []}
            onChange={(ids) => onParamChange("courseIds", ids)}
            placeholder="All Courses"
            maxWidth={160}
          />
        )}

        {/* Batch — server-side type-ahead by batch name or code */}
        <div ref={batchBoxRef} style={{ position: "relative" }}>
          <input
            type="text"
            placeholder="Batch name / code"
            value={searchParams.batchCode || ""}
            autoComplete="off"
            onChange={(e) => {
              onParamChange("batchCode", e.target.value);
              setShowBatchDropdown(true);
            }}
            onKeyDown={handleKeyDown}
            onFocus={(e) => {
              onInputFocus(e);
              setShowBatchDropdown(true);
            }}
            onBlur={onInputBlur}
            style={{ ...toolbarInputStyle, width: 160 }}
          />
          {showBatchDropdown && (searchParams.batchCode || "").trim().length >= 2 && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                zIndex: 30,
                minWidth: 220,
                maxWidth: 320,
                maxHeight: 260,
                overflowY: "auto",
                background: "var(--surface-card)",
                border: "1px solid var(--border-strong)",
                borderRadius: 8,
                boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
              }}
            >
              {batchLoading ? (
                <div style={{ padding: "8px 12px", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                  Searching…
                </div>
              ) : batchSuggestions.length === 0 ? (
                <div style={{ padding: "8px 12px", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                  No matching batches
                </div>
              ) : (
                batchSuggestions.map((b) => (
                  <div
                    key={b.id}
                    onMouseDown={(e) => {
                      // onMouseDown (not onClick) so the input's blur doesn't
                      // dismiss the list before the selection registers.
                      e.preventDefault();
                      selectBatch(b);
                    }}
                    style={{
                      padding: "7px 12px",
                      fontSize: "0.82rem",
                      color: "var(--text-strong)",
                      cursor: "pointer",
                      borderBottom: "1px solid var(--border-subtle)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-page)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "var(--surface-card)")}
                    title={batchLabel(b)}
                  >
                    {batchLabel(b)}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Search Type (Enquiry Type) - Multi-select */}
        <MultiSelect
          options={(lookups?.searchTypes || []).filter((t: any) => t.id !== -100)}
          selectedIds={searchParams.searchTypeIds || []}
          onChange={(ids) => onParamChange("searchTypeIds", ids)}
          placeholder="All Types"
          maxWidth={160}
        />

        {/* Action buttons — uniform 34×34 chips, same height as inputs */}
        {(() => {
          /* eslint-disable @typescript-eslint/no-explicit-any */
          const actionBtnStyle = (bg: string, fg: string = "#fff"): React.CSSProperties => ({
            width: 34,
            height: 34,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: bg,
            color: fg,
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 13,
            transition: "opacity .12s, transform .06s",
            boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
          });
          const hover = (e: any) => (e.currentTarget.style.opacity = "0.85");
          const unhover = (e: any) => (e.currentTarget.style.opacity = "1");
          return (
            <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
              {staffWebforms.length > 0 && (
                <div ref={menuRef} style={{ position: "relative" }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (staffWebforms.length === 1) {
                        window.open(`/webform/${staffWebforms[0].id}`, "_blank");
                      } else {
                        setShowNewEnquiryMenu((p) => !p);
                      }
                    }}
                    title="New Enquiry"
                    style={actionBtnStyle("#16a34a")}
                    onMouseEnter={hover}
                    onMouseLeave={unhover}
                  >
                    <i className="fa fa-plus" />
                  </button>
                  {showNewEnquiryMenu && staffWebforms.length > 1 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        marginTop: 6,
                        background: "var(--surface-card)",
                        border: "1px solid var(--border-default)",
                        borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
                        zIndex: 1000,
                        minWidth: 220,
                        overflow: "hidden",
                      }}
                    >
                      {staffWebforms.map((w: any) => (
                        <div
                          key={w.id}
                          onClick={() => {
                            window.open(`/webform/${w.id}`, "_blank");
                            setShowNewEnquiryMenu(false);
                          }}
                          style={{
                            padding: "10px 14px",
                            cursor: "pointer",
                            fontSize: "0.85rem",
                            color: "var(--text-strong)",
                            borderBottom: "1px solid var(--border-subtle)",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-page)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "var(--surface-card)")}
                        >
                          {w.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={onSearch}
                disabled={loading}
                title="Search"
                style={{ ...actionBtnStyle("#2563eb"), opacity: loading ? 0.7 : 1 }}
                onMouseEnter={hover}
                onMouseLeave={unhover}
              >
                <i className="fa fa-search" />
              </button>
              <button
                type="button"
                onClick={onPasswordSearch}
                title="Password Search"
                style={actionBtnStyle("#0891b2")}
                onMouseEnter={hover}
                onMouseLeave={unhover}
              >
                <i className="fa fa-key" />
              </button>
              <button
                type="button"
                onClick={onExcel}
                title="Export Excel"
                style={actionBtnStyle("#16a34a")}
                onMouseEnter={hover}
                onMouseLeave={unhover}
              >
                <i className="fa fa-file-excel-o" />
              </button>
              <button
                type="button"
                onClick={onBulkUpdate}
                title="Bulk Update via Excel"
                style={actionBtnStyle("#7c3aed")}
                onMouseEnter={hover}
                onMouseLeave={unhover}
              >
                <i className="fa fa-upload" />
              </button>
              <button
                type="button"
                onClick={onBulkStatusChange}
                title="Change Enrollment Status (Bulk)"
                style={actionBtnStyle("#d97706")}
                onMouseEnter={hover}
                onMouseLeave={unhover}
              >
                <i className="fa fa-exchange" />
              </button>
              <button
                type="button"
                onClick={() => onBulkMessage("whatsapp")}
                title="WhatsApp"
                style={actionBtnStyle("#25d366")}
                onMouseEnter={hover}
                onMouseLeave={unhover}
              >
                <i className="fa fa-whatsapp" />
              </button>
              <button
                type="button"
                onClick={() => onBulkMessage("sms")}
                title="SMS"
                style={actionBtnStyle("#64748b")}
                onMouseEnter={hover}
                onMouseLeave={unhover}
              >
                <i className="fa fa-comment" />
              </button>
              <button
                type="button"
                onClick={() => onBulkMessage("email")}
                title="Email"
                style={actionBtnStyle("#2563eb")}
                onMouseEnter={hover}
                onMouseLeave={unhover}
              >
                <i className="fa fa-envelope" />
              </button>
              <button
                type="button"
                onClick={() => onBulkMessage("notification")}
                title="Notification"
                style={actionBtnStyle("#eab308", "#1f2937")}
                onMouseEnter={hover}
                onMouseLeave={unhover}
              >
                <i className="fa fa-bell" />
              </button>

              {/* Bulk Actions menu — placed right after the notification (bell) chip.
                  A single entry point that surfaces the full legacy operation set. */}
              <div ref={bulkMenuRef} style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setShowBulkMenu((p) => !p)}
                  title="Bulk Actions"
                  style={{
                    ...actionBtnStyle("#0f172a"),
                    width: "auto",
                    padding: "0 12px",
                    gap: 6,
                    fontWeight: 600,
                  }}
                  onMouseEnter={hover}
                  onMouseLeave={unhover}
                >
                  <i className="fa fa-bolt" />
                  <span>Bulk Actions</span>
                  <i className="fa fa-caret-down" style={{ opacity: 0.8 }} />
                </button>

                {showBulkMenu && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: 0,
                      marginTop: 6,
                      background: "var(--surface-card)",
                      border: "1px solid var(--border-default)",
                      borderRadius: 10,
                      boxShadow: "0 12px 32px rgba(15,23,42,0.16)",
                      zIndex: 1000,
                      width: 300,
                      maxHeight: 460,
                      overflowY: "auto",
                      padding: "6px 0",
                    }}
                  >
                    {/* Header with the live selected count */}
                    <div
                      style={{
                        padding: "8px 14px 10px",
                        borderBottom: "1px solid var(--border-subtle)",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: "var(--text-body)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>Bulk Actions</span>
                      <span style={{ color: "var(--text-strong)" }}>{selectedCount} selected</span>
                    </div>

                    {BULK_ACTION_GROUPS.filter(
                      // Academic-module actions are hidden when the tenant's Academics
                      // resource (id 112) is off — mirrors legacy BulkOperationButtons.jsp
                      // `<logic:present name="programBased">`.
                      (grp) => grp.group !== "Academics" || !!lookups?.academicsEnabled,
                    ).map((grp) => (
                      <div key={grp.group}>
                        <div
                          style={{
                            padding: "8px 14px 4px",
                            fontSize: "0.62rem",
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            color: "var(--text-faint)",
                          }}
                        >
                          {grp.group}
                        </div>
                        {grp.items.map((it) => {
                          const enabled = !!it.enabled;
                          return (
                            <div
                              key={it.key}
                              onClick={() => enabled && runBulkAction(it.key)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "8px 14px",
                                cursor: enabled ? "pointer" : "default",
                                color: enabled ? "#0f172a" : "#94a3b8",
                                fontSize: "0.85rem",
                              }}
                              onMouseEnter={(e) => {
                                if (enabled) e.currentTarget.style.background = "var(--surface-page)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "var(--surface-card)";
                              }}
                            >
                              <i
                                className={`fa ${it.icon}`}
                                style={{ width: 16, textAlign: "center", color: enabled ? "#475569" : "#cbd5e1" }}
                              />
                              <span style={{ flex: 1 }}>{it.label}</span>
                              {enabled ? (
                                <i className="fa fa-check" style={{ color: "var(--status-success)", fontSize: 12 }} />
                              ) : (
                                <span
                                  style={{
                                    fontSize: "0.62rem",
                                    fontWeight: 600,
                                    color: "var(--text-muted)",
                                    background: "var(--surface-sunken)",
                                    border: "1px solid var(--border-default)",
                                    borderRadius: 8,
                                    padding: "1px 7px",
                                  }}
                                >
                                  Soon
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default SearchToolbar;
